import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiPut } from '../../utils/apiFetch';
import apiPath from '../../utils/apiPath';
import useToastContext from 'hooks/useToastContext';
import OInputField from 'components/reusable/OInputField';
import { useForm } from 'react-hook-form';
import dayjs from 'dayjs';

const PayrollEdit = ({ setPayrollEdit, currentItem, getPayrollList }) => {
  const { t } = useTranslation();
  const notification = useToastContext();
  const [loading, setLoading] = useState(false);

  // ═══ NEW: Helper to get status color for the dropdown ═══
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'processing': return 'text-blue-600 bg-blue-50';
      case 'completed': return 'text-green-600 bg-green-50';
      case 'failed': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      regularHours: currentItem?.regularHours || 0,
      overtimeHours: currentItem?.overtimeHours || 0,
      doubleTimeHours: currentItem?.doubleTimeHours || 0,
      regularPay: currentItem?.regularPay || 0,
      overtimePay: currentItem?.overtimePay || 0,
      grossPay: currentItem?.grossPay || 0,
      netPay: currentItem?.netPay || 0,
      status: currentItem?.status || 'pending'
    }
  });

  // Watch status for real-time UI updates in the dropdown
  const selectedStatus = watch('status');

  useEffect(() => {
    if (currentItem) {
      // Helper to safely round to 2 decimal places
      const toTwoDecimals = (num) => {
        if (num === undefined || num === null) return 0;
        return Number(num).toFixed(2);
      };

      reset({
        regularHours: toTwoDecimals(currentItem.regularHours),
        overtimeHours: toTwoDecimals(currentItem.overtimeHours),
        doubleTimeHours: toTwoDecimals(currentItem.doubleTimeHours),
        regularPay: toTwoDecimals(currentItem.regularPay),
        overtimePay: toTwoDecimals(currentItem.overtimePay),
        grossPay: toTwoDecimals(currentItem.grossPay),
        netPay: toTwoDecimals(currentItem.netPay),
        status: currentItem.status || 'pending'
      });
    }
  }, [currentItem, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      // Send status along with the rest of the data
      const res = await apiPut(`${apiPath.payrollEdit}/${currentItem._id}`, data);
      if (res?.data?.success) {
        notification.success('Payroll updated successfully');
        getPayrollList();
        setPayrollEdit(false);
      } else {
        notification.error(res?.data?.message || 'Failed to update payroll');
      }
    } catch (error) {
      console.error('Error updating payroll:', error);
      notification.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative w-auto my-6 mx-auto max-w-3xl">
          <form onSubmit={handleSubmit(onSubmit)} className="sm:py-4 sm:px-2 py-8 px-7">
            <div className="border-0 rounded-lg shadow-lg relative flex flex-col w-full min-w-[800px] max-h-[96vh] overflow-y-auto bg-white outline-none focus:outline-none">
              <div className="flex items-center justify-between p-5 border-b border-solid border-slate-200 rounded-t">
                <h3 className="text-xl font-semibold">Edit Payroll</h3>
                <button 
                  className="ml-auto flex items-center justify-center text-3xl text-black border-2 rounded-full h-8 w-8 float-right leading-none font-extralight outline-none focus:outline-none" 
                  onClick={() => setPayrollEdit(false)}
                >
                  <span className="text-[#B8BBBF] text-4xl" title="Close">×</span>
                </button>
              </div>
              
              <div className="relative p-6 flex-auto">
                <div className="mb-4">
                  <h4 className="font-semibold mb-2">{currentItem?.employee?.name} ({currentItem?.employee?.employeeId})</h4>
                  <p className="text-sm text-gray-500">
                    {dayjs(currentItem?.payPeriod?.startDate).format("MM-DD-YYYY")} to {dayjs(currentItem?.payPeriod?.endDate).format("MM-DD-YYYY")}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <OInputField
                    inputLabel="Regular Hours"
                    type="text"
                    name="regularHours"
                    register={register('regularHours', { required: 'Required' })}
                    errors={errors}
                  />
                  <OInputField
                    inputLabel="Overtime Hours"
                    type="text"
                    name="overtimeHours"
                    register={register('overtimeHours')}
                    errors={errors}
                  />
                  <OInputField
                    inputLabel="Regular Pay ($)"
                    type="text"
                    name="regularPay"
                    register={register('regularPay', { required: 'Required' })}
                    errors={errors}
                  />
                  <OInputField
                    inputLabel="Overtime Pay ($)"
                    type="text"
                    name="overtimePay"
                    register={register('overtimePay')}
                    errors={errors}
                  />
                  <OInputField
                    inputLabel="Gross Pay ($)"
                    type="text"
                    name="grossPay"
                    register={register('grossPay', { required: 'Required' })}
                    errors={errors}
                  />
                  <OInputField
                    inputLabel="Net Pay ($)"
                    type="text"
                    name="netPay"
                    register={register('netPay', { required: 'Required' })}
                    errors={errors}
                  />
                </div>

                {/* ═══ NEW: Status Dropdown ═══ */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payroll Status
                  </label>
                  <select
                    {...register('status')}
                    className={`block w-full py-3 px-4 text-sm border-2 rounded-lg focus:outline-none focus:ring-0 ${getStatusColor(selectedStatus)}`}
                  >
                    <option key='pending' value="pending">Pending</option>
                    <option key='processing' value="processing">Processing</option>
                    <option key='completed' value="completed">Completed</option>
                    <option key='failed' value="failed">Failed</option>
                  </select>
                </div>
              </div>

              <div className="border-t border-solid border-slate-200 rounded-b flex items-center justify-center p-6">
                <button 
                  onClick={() => setPayrollEdit(false)} 
                  className="text-black bg-[#E1E1E1] font-normal px-12 py-2.5 text-sm outline-none focus:outline-none rounded mr-6" 
                  type="button"
                >
                  Cancel
                </button>
                <button 
                  className="bg-LightBlue text-white font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none" 
                  type="submit" 
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className="opacity-25 fixed inset-0 z-40 bg-black" />
    </>
  );
};

export default PayrollEdit;